import { Component, inject, OnInit, signal } from "@angular/core";
import { NgbAccordionModule, NgbPaginationModule } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe } from "@ngx-translate/core";
import { forkJoin } from "rxjs";

import { TranslateTokenPipe } from "../../../core/pipes";
import { AdaaHelper } from "../../../core/utils";
import { Language } from "../../../shared/constants/enums";
import { TotalRows } from "../../../shared/models";
import { Faq, FaqCategory, FaqQuestionType } from "../../../shared/models/online-help-model";
import { LanguageService } from "../../../shared/services";
import { OnlineHelpService } from "../../../shared/services/online-help-service";

@Component({
  selector: "adaa-faq-questions",
  imports: [NgbPaginationModule, NgbAccordionModule, TranslatePipe, TranslateTokenPipe],
  templateUrl: "./faq-questions.html",
  styleUrl: "./faq-questions.scss",
})
export class FaqQuestionsComponent implements OnInit {
  private _onlineHelpservice = inject(OnlineHelpService);
  languageService = inject(LanguageService);

  offset: number = 0;
  currentPage: number = 1;
  totalRows: number = 0;
  limit: number = 10;
  categories = signal<FaqCategory[]>([]);
  questions = signal<Faq[]>([]);
  selectedCategoryId: number | undefined;
  searchFields = signal<FaqQuestionType | undefined>(undefined);

  public ngOnInit(): void {
    forkJoin({
      categories: this._onlineHelpservice.getFAQSearchCategories(),
      questions: this._onlineHelpservice.getFAQCategoryQuestions(this.offset),
    }).subscribe((res) => {
      this.categories.set(res.categories.responseData.filter((category) => category.totalFaqs > 0));
      this.questions.set(res.questions.responseData);
      this.totalRows = (res.questions.additionalFields as TotalRows).totalOfRows;
    });
  }

  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  public onPageChange(event: any): void {
    const target = event.target as HTMLElement;
    const pageItem = target.closest(".page-item");
    const anchor = target.closest("a");

    if (!pageItem || !anchor) return;

    const isDisabled = pageItem.classList.contains("disabled");
    const ariaLabel = anchor.getAttribute("aria-label");
    if (ariaLabel !== "Next" && ariaLabel !== "Previous" && isDisabled) return;

    if (ariaLabel === "Next") {
      if (this.currentPage < this.totalRows) this.currentPage += 1;
    } else if (ariaLabel === "Previous") {
      if (this.currentPage > 1) this.currentPage -= 1;
    } else {
      const pageNumber = Number(anchor.textContent?.trim());
      if (!isNaN(pageNumber)) {
        this.currentPage = pageNumber;
      }
    }

    this._doPagination();
  }

  private _doPagination() {
    this.offset = this.limit * (this.currentPage - 1);
    this._onlineHelpservice.getFAQCategoryQuestions(this.offset).subscribe((res) => {
      this.questions.set(res.responseData);
      this.totalRows = (res.additionalFields as TotalRows).totalOfRows;
    });
  }

  public performSearch(event: Event) {
    const input = (event.target as HTMLInputElement).value;
    const key = `question_${AdaaHelper.getCurrentLang() === Language.Arabic ? "ae" : "en"}`;

    const searchFields = {
      [key]: {
        value: input,
      },
    } as FaqQuestionType;
    this.searchFields.set(searchFields);

    this._onlineHelpservice
      .getFAQCategoryQuestions(0, this.selectedCategoryId ? this.selectedCategoryId : undefined, {
        searchFields: searchFields,
      })
      .subscribe((res) => {
        this.questions.set(res.responseData);
        this.totalRows = (res.additionalFields as TotalRows).totalOfRows;
        this.currentPage = 1;
      });
  }

  public filter(id?: number) {
    this.selectedCategoryId = id ? id : undefined;
    this._onlineHelpservice
      .getFAQCategoryQuestions(
        0,
        id ? id : undefined,
        this.searchFields()
          ? {
              searchFields: this.searchFields()!,
            }
          : undefined
      )
      .subscribe((res) => {
        this.questions.set(res.responseData);
        this.totalRows = (res.additionalFields as TotalRows).totalOfRows;
      });
  }

  public getLabel(categoryId: number) {
    const category = this.categories().find((category) => category.id === categoryId);
    if (category) return AdaaHelper.getItemValueByToken(category, "name");
    return "";
  }
}
